"use client";
/**
 * The files waiting on the call (attach on the call, 2026-09-24; mock of
 * record mockups/voice-attach-2026-09-24.html): the typing box's own
 * AttachmentStrip on a white rounded card between the ball and the tray, and
 * one quiet line under it. The card opens by growing its row on the tray's
 * spring; the stage stands on its foot, so the ball lifts once to make room
 * and nothing else moves. It keeps the last chips while it folds shut, so the
 * ball settles back rather than dropping. A polite live region, outside the
 * fold, says how many landed once none is still uploading (the mock's words).
 */
import { useState } from "react";
import { AttachmentStrip } from "@/components/message-input";
import type { PendingAttachment } from "@/components/message-input/AttachmentHandler";
import { cn } from "@/lib/utils";

export const FILES_HINT = "Say what to do with them";
/** What a reader hears when files land on the card. */
export const filesAttached = (n: number) =>
  `${n === 1 ? "1 file" : `${n} files`} attached. ${FILES_HINT}.`;

export function LineFiles({
  rows,
  removable,
  onRemove,
}: {
  rows: PendingAttachment[];
  removable: (id: string) => boolean;
  onRemove: (id: string) => void;
}) {
  const up = rows.length > 0;
  const [shown, setShown] = useState(rows);
  if (up && shown !== rows) setShown(rows);
  const list = up ? rows : shown;
  const landed = rows.filter((r) => r.status === "complete").length;
  const busy = rows.some((r) => r.status === "processing");
  return (
    <>
      <div
        className={cn(
          "cl-files grid w-max [transition:grid-template-rows_0.6s_var(--cl-spring)]",
          up ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
        data-files={up ? "" : undefined}
        // Folded, the last chips are only what the fold shows leaving.
        inert={!up}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="flex justify-center px-4 pt-3.5 pb-4">
            <div
              className={cn(
                "flex max-w-[min(360px,calc(100vw_-_4rem),calc(var(--site-task-col-measured-width,100vw)_-_2rem))] flex-col items-center gap-2 rounded-20 bg-bg-elevated px-3.5 pt-3 pb-2.5 shadow-(--cl-tray-shade) [transition:opacity_0.3s_var(--ease-smooth),translate_0.5s_var(--cl-spring),scale_0.5s_var(--cl-spring)]",
                up ? "opacity-100" : "translate-y-2 scale-[0.98] opacity-0"
              )}
              // The Mac panel grants clicks only inside what is on screen.
              data-dock-hit={up ? "" : undefined}
            >
              <AttachmentStrip
                pendingAttachments={list}
                imageError={null}
                onRemove={onRemove}
                filenameMaxWidth={170}
                className="justify-center border-b-0 p-0"
                canRemove={(a) => removable(a.id)}
              />
              <p className="text-3 font-medium text-fg-muted">{FILES_HINT}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Mounted with the card and empty until files land, so the words are
        announced; outside the fold, which is inert while shut. */}
      <p className="sr-only" aria-live="polite">
        {!busy && landed > 0 ? filesAttached(landed) : ""}
      </p>
    </>
  );
}
